/**
 * Track Event Service
 *
 * High-level event tracking with automatic enrichment.
 * Mirrors Freya's useTrackEvent composable for end-to-end consistency.
 *
 * Enrichment includes:
 * - page_path, page_title
 * - referring_url, referring_domain
 * - utm_source, utm_medium, utm_campaign
 *
 * Automatic conversion routing:
 * - trial_signup_intent → adTracking.trackConversion('SignUp', ...)
 * - generate_lead → adTracking.trackConversion('Lead', ...)
 * - onboarding_complete → adTracking.trackConversion('CompleteRegistration', ...)
 */

import type { TrackingAnalyticsService } from './TrackingAnalyticsService'
import type { AdTrackingService } from './AdTrackingService'
import type { Route } from 'vue-router'

interface EventParams {
  [key: string]: string | number | boolean | undefined
}

const isDev = process.env.NODE_ENV === 'development'

export class TrackEventService {
  private analyticsService: TrackingAnalyticsService
  private adTrackingService: AdTrackingService | null

  constructor(
    analyticsService: TrackingAnalyticsService,
    adTrackingService: AdTrackingService | null
  ) {
    this.analyticsService = analyticsService
    this.adTrackingService = adTrackingService
  }

  /**
   * Track an event with automatic enrichment and conversion routing.
   * This is the primary method for tracking events — mirrors Freya's trackEvent().
   */
  trackEvent(eventName: string, eventParams: EventParams = {}, route?: Route): void {
    try {
      // Build enriched params
      const params: EventParams = {
        page_path:
          route?.fullPath || (typeof window !== 'undefined' ? window.location.pathname : ''),
        page_title: typeof document !== 'undefined' ? document.title : '',
        ...eventParams
      }

      // Add referrer information
      if (typeof document !== 'undefined') {
        const referrer = document.referrer || ''
        if (referrer) {
          params.referring_url = referrer
          try {
            params.referring_domain = new URL(referrer).hostname
          } catch {
            // Invalid URL, skip
          }
        }
      }

      // Add UTM parameters from route query
      if (route?.query) {
        if (route.query.utm_source) params.utm_source = route.query.utm_source as string
        if (route.query.utm_medium) params.utm_medium = route.query.utm_medium as string
        if (route.query.utm_campaign) params.utm_campaign = route.query.utm_campaign as string
      }

      // Track via analytics providers (GA4, PostHog)
      this.analyticsService.track(eventName, params)

      // Route to ad conversion tracking based on event name
      // This mirrors Freya's useTrackEvent logic for end-to-end consistency
      if (this.adTrackingService) {
        if (eventName === 'trial_signup_intent') {
          this.adTrackingService.trackConversion('SignUp', {
            provider: params.provider,
            source: params.source,
            plan: params.plan
          })
        }

        if (eventName === 'generate_lead') {
          this.adTrackingService.trackConversion('Lead', {
            currency: params.currency,
            value: params.value,
            form_id: params.form_id,
            form_name: params.form_name
          })
        }

        if (eventName === 'onboarding_complete') {
          this.adTrackingService.trackConversion('CompleteRegistration', {
            provider: params.provider,
            owner: params.owner
          })
        }
      }

      if (isDev) {
        console.log(`[TrackEvent] ${eventName}`, params)
      }
    } catch (error) {
      if (isDev) {
        console.error('Error tracking event:', error)
      }
    }
  }
}
